import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { StaticListings } from '@/types/StaticListings';
import type { UnitFeatures } from '@/types/UnitFeatures';
import { Asterisk, Minus, MinusCircle, Plus } from 'lucide-react';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import Helper from '@/lib/helper';
import LabelWithAsterisk from '@/components/LabelWithAsterisk';
import BadgeSelect from '@/components/BadgeSelect';

interface UnitDetailsProps {
    selectedListingType: number | null;
    electricityPhases: StaticListings[];
    unitFeatures: UnitFeatures[] | null,
    furnishingConditions: StaticListings[];

    noOfBathrooms: number | null;
    setNoOfBathrooms: React.Dispatch<React.SetStateAction<number | null>>;
    noOfBedrooms: number | null;
    setNoOfBedrooms: React.Dispatch<React.SetStateAction<number | null>>;
    noOfMaidRooms: number | null;
    setNoOfMaidRooms: React.Dispatch<React.SetStateAction<number | null>>;
    noOfParkingSpots: number | null;
    setNoOfParkingSpots: React.Dispatch<React.SetStateAction<number | null>>;

    floorLoading: string;
    setFloorLoading: React.Dispatch<React.SetStateAction<string>>;
    selectedFloorLoadingUnit: string;
    setSelectedFloorLoadingUnit: React.Dispatch<React.SetStateAction<string>>;
    ceilingHeight: string;
    setCeilingHeight: React.Dispatch<React.SetStateAction<string>>;
    selectedCeilingHeightUnit: string;
    setSelectedCeilingHeightUnit: React.Dispatch<React.SetStateAction<string>>;

    builtUp: string;
    setBuiltUp: React.Dispatch<React.SetStateAction<string>>;
    selectedBuiltUpUnit: string;
    setSelectedBuiltUpUnit: React.Dispatch<React.SetStateAction<string>>;
    floorDetails: {
        block: string;
        floor: string;
        unit: string;
    };
    setFloorDetails: React.Dispatch<React.SetStateAction<{
        block: string;
        floor: string;
        unit: string;
    }>>;
    builtUpDimensions: { 
        width: string; 
        length: string 
    };
    setBuiltUpDimensions: React.Dispatch<React.SetStateAction<{ 
        width: string; 
        length: string 
    }>>;
    selectedBuiltUpDimensionUnit: string;
    setSelectedBuiltUpDimensionUnit: React.Dispatch<React.SetStateAction<string>>;
    landArea: string;
    setLandArea: React.Dispatch<React.SetStateAction<string>>;
    selectedLandAreaUnit: string;
    setSelectedLandAreaUnit: React.Dispatch<React.SetStateAction<string>>;
    landAreaDimensions: { 
        width: string; 
        length: string 
    };
    setLandAreaDimensions: React.Dispatch<React.SetStateAction<{ 
        width: string; 
        length: string 
    }>>;
    selectedLandAreaDimensionUnit: string;
    setSelectedLandAreaDimensionUnit: React.Dispatch<React.SetStateAction<string>>;
    selectedUnitFeatures: number[];
    setSelectedUnitFeatures: React.Dispatch<React.SetStateAction<number[]>>;
    selectedFurnishingCondition: number | null;
    setSelectedFurnishingCondition: React.Dispatch<React.SetStateAction<number | null>>;
    selectedElectricityPhase: number | null;
    setSelectedElectricityPhase: React.Dispatch<React.SetStateAction<number | null>>;
    electricitySupply: number | null;
    setElectricitySupply: React.Dispatch<React.SetStateAction<number | null>>;
    selectedElectricitySupplyUnit: string;
    setSelectedElectricitySupplyUnit: React.Dispatch<React.SetStateAction<string>>;

    totalCargoLifts: number | null;
    setTotalCargoLifts: React.Dispatch<React.SetStateAction<number | null>>;
    totalPassengerLifts: number | null;
    setTotalPassengerLifts: React.Dispatch<React.SetStateAction<number | null>>;
    maximumLiftCapacity: number | null;
    setMaximumLiftCapacity: React.Dispatch<React.SetStateAction<number | null>>;

    selectedPropertyType: number | null;
    selectedPropertySubType: number | null;

    builtUpUnits: StaticListings[];
    landAreaUnits: StaticListings[];
    builtUpDimensionUnits: StaticListings[];
    landAreaDimensionUnits: StaticListings[];
    floorLoadingUnits: StaticListings[];
    electricitySupplyUnits: StaticListings[];
    ceilingHeightUnits: StaticListings[];

    hideBlockNoWhenPublished: boolean;
    setHideBlockNoWhenPublished: React.Dispatch<React.SetStateAction<boolean>>;
    markAsDuplicate?: boolean;
    edit?: boolean;
}

export interface UnitDetailsRef {
    callback: () => boolean;
}

const UnitDetails = forwardRef<UnitDetailsRef, UnitDetailsProps>(({
    selectedListingType,
    unitFeatures,
    furnishingConditions,
    electricityPhases,
    noOfBathrooms,
    setNoOfBathrooms,
    noOfBedrooms,
    setNoOfBedrooms,
    noOfMaidRooms,
    setNoOfMaidRooms,
    noOfParkingSpots,
    setNoOfParkingSpots,
    builtUp,
    setBuiltUp,
    selectedBuiltUpUnit,
    setSelectedBuiltUpUnit,
    floorDetails,
    setFloorDetails,
    builtUpDimensions,
    setBuiltUpDimensions,
    selectedBuiltUpDimensionUnit,
    setSelectedBuiltUpDimensionUnit,
    landArea,
    setLandArea,
    selectedLandAreaUnit,
    setSelectedLandAreaUnit,
    landAreaDimensions,
    setLandAreaDimensions,
    selectedLandAreaDimensionUnit,
    setSelectedLandAreaDimensionUnit,
    selectedUnitFeatures,
    setSelectedUnitFeatures,
    selectedFurnishingCondition,
    setSelectedFurnishingCondition,
    selectedElectricityPhase,
    setSelectedElectricityPhase,
    electricitySupply,
    setElectricitySupply,
    selectedElectricitySupplyUnit,
    setSelectedElectricitySupplyUnit,
    totalCargoLifts,
    setTotalCargoLifts,
    totalPassengerLifts,
    setTotalPassengerLifts,
    maximumLiftCapacity,
    setMaximumLiftCapacity,
    selectedPropertyType,
    selectedPropertySubType,
    hideBlockNoWhenPublished,
    setHideBlockNoWhenPublished,
    floorLoading,
    setFloorLoading,
    selectedFloorLoadingUnit,
    setSelectedFloorLoadingUnit,
    ceilingHeight,
    setCeilingHeight,
    selectedCeilingHeightUnit,
    setSelectedCeilingHeightUnit,
    markAsDuplicate,
    edit,
    builtUpUnits,
    landAreaUnits,
    builtUpDimensionUnits,
    landAreaDimensionUnits,
    floorLoadingUnits,
    electricitySupplyUnits,
    ceilingHeightUnits
}, ref) => {
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const landAreaBuiltUpRef = useRef(null);
    const sizeBuiltUpRef = useRef(null);
    const bathroomRef = useRef(null);
    const bedroomRef = useRef(null);
    const furnishingRef = useRef(null);

    const callback = () => {
        try {
            let valErrors = [];

            if(sizeBuiltUpRef.current && !builtUp) {
                valErrors.push('builtUp');
            }
          
            if(landAreaBuiltUpRef.current && !landArea) {
                valErrors.push('landArea');
            }

            // if(furnishingRef.current && !selectedFurnishingCondition) {
            //     valErrors.push('furnishing');
            // }

            if(bathroomRef.current && noOfBathrooms === null) {
                valErrors.push('bathrooms');
            }

            if(bedroomRef.current && noOfBedrooms === null) {
                valErrors.push('bedrooms');
            }
            setValidationErrors(valErrors);
            if(valErrors.length > 0) {
                throw new Error('Please complete all required fields.');
            }
            return true;
        } catch (err : any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            return false
        }
    }

    const removeError = (field: string) => {
        setValidationErrors(prevErrors => prevErrors.filter(x => x !== field));
    };

    useImperativeHandle(ref, () => ({
        callback
    }));

    return (
        <section>
            {
                selectedPropertySubType && ![4, 18, 30, 32, 38, 39].includes(selectedPropertySubType) && (
                    <>
                        <div className="mt-3 pt-5" ref={bathroomRef}>
                            <div className='flex justify-between'>
                                <LabelWithAsterisk label='No of Bathrooms' className='text-black'/>
                                <div className="flex items-center justify-end gap-2">
                                    <Button 
                                    type="button" 
                                    variant="outline" 
                                    size="icon" 

                                    disabled={!noOfBathrooms || markAsDuplicate || edit}
                                    onClick={() => setNoOfBathrooms(prev => (prev && prev > 0 ? prev - 1 : 0))}>
                                        <Minus />
                                    </Button>
                                    <Button variant='ghost' className="pointer-events-none text-black">{noOfBathrooms ?? 'Select'}</Button>
                                    <Button 
                                    type="button" 
                                    variant="outline" 
                                    size="icon" 

                                    onClick={() => {
                                        removeError('bathrooms');
                                        setNoOfBathrooms(prev => (prev === null ? 0 : prev + 1));
                                    }} 
                                    disabled={markAsDuplicate || edit}>
                                        <Plus />
                                    </Button>
                                </div>
                            </div>
                            {
                                validationErrors.includes('bathrooms') && (
                                    <div className='text-end'>
                                        <span className='text-xs text-red-400'>No of bathrooms is required.</span>
                                    </div>
                                )
                            }
                        </div>
                        {
                            selectedListingType === 1 && (
                                <>
                                    <div className="mt-3 pt-5" ref={bedroomRef}>
                                        <div className='flex justify-between'>
                                            <LabelWithAsterisk label='No of Bedrooms' className='text-black'/>
                                            <div className="flex items-center justify-end gap-2">
                                                <Button 
                                                type="button" 
                                                variant="outline" 
                                                size="icon" 

                                                disabled={!noOfBedrooms || markAsDuplicate || edit}
                                                onClick={() => setNoOfBedrooms(prev => (prev && prev > 0 ? prev - 1 : 0))}>
                                                    <Minus />
                                                </Button>
                                                <Button variant='ghost' className="pointer-events-none text-black">{noOfBedrooms ?? 'Select'}</Button>
                                                <Button 
                                                type="button" 
                                                variant="outline" 
                                                size="icon" 

                                                onClick={() => {
                                                    removeError('bedrooms');
                                                    setNoOfBedrooms(prev => (prev === null ? 0 : prev + 1));
                                                }} 
                                                disabled={markAsDuplicate || edit}>
                                                    <Plus />
                                                </Button>
                                            </div>
                                        </div>
                                        {
                                            validationErrors.includes('bedrooms') && (
                                                <div className='text-end'>
                                                    <span className='text-xs text-red-400'>No of bedrooms is required.</span>
                                                </div>
                                            )
                                        }
                                    </div>
                                    <div className="mt-3 pt-5 flex items-center justify-between">
                                        <Label className="text-black">No of maidrooms</Label>
                                        <div className="flex items-center justify-end gap-2">
                                            <Button 
                                            type="button" 
                                            variant="outline" 
                                            size="icon" 

                                            disabled={!noOfMaidRooms || markAsDuplicate || edit}
                                            onClick={() => setNoOfMaidRooms(prev => (prev && prev > 0 ? prev - 1 : 0))}>
                                                <Minus />
                                            </Button>
                                            <Button variant='ghost' className="pointer-events-none text-black">{noOfMaidRooms ?? 'Select'}</Button>
                                            <Button 
                                            type="button" 
                                            variant="outline" 
                                            size="icon" 

                                            disabled={markAsDuplicate || edit}
                                            onClick={() => setNoOfMaidRooms(prev => (prev === null ? 0 : prev + 1))} >
                                                <Plus />
                                            </Button>
                                        </div>
                                    </div>
                                </>
                            )
                        }
                        <hr className='my-5'/>
                        <h3 className='h5'>Size</h3>
                        {
                            ![4, 18, 30, 32, 38, 39].includes(selectedPropertySubType)
                            && (
                                <>
                                    <div className='mt-3 pt-5'>
                                        <div className="md:flex justify-between">
                                            <LabelWithAsterisk label={'Built-up'} className='text-black'/>
                                            <div className="w-full md:w-1/2 mt-3 md:mt-0">
                                                <div className='relative'>
                                                    <Input
                                                    ref={sizeBuiltUpRef}
                                                    className={`${selectedBuiltUpUnit === 'sqft' ? 'pr-18' : 'pr-20'} text-end text-xs ${validationErrors.includes('builtUp') && 'border-red-400'}`}
                                                    placeholder="What is the build up?"
                                                    value={Helper.formatWithDecimal(Number(builtUp))}
                                                    onKeyDown={Helper.allowOnlyNumbers}
                                                    onFocus={Helper.handleFocusToEnd}
                                                    onClick={Helper.handleFocusToEnd}
                                                    onChange={(e) => {
                                                        const value = Helper.handleCurrencyChange(e);
                                                        setBuiltUp(value);
                                                        removeError('builtUp');
                                                    }}
                                                    disabled={markAsDuplicate || edit}/>
                                                    <div className="absolute inset-y-0 right-2 flex items-center">
                                                        <BadgeSelect
                                                        options={builtUpUnits}
                                                        value={selectedBuiltUpUnit}
                                                        onChange={setSelectedBuiltUpUnit}
                                                        className="absolute inset-y-0 right-2"/>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {
                                            validationErrors.includes('builtUp') && (
                                                <div className='text-end'>
                                                    <span className='text-xs text-red-400 '>Built up is required.</span>
                                                </div>
                                            )
                                        }
                                    </div>
                                    
                                    <div className="mt-3 pt-5 md:flex justify-between items-center">
                                        <Label className="text-black">Dimensions</Label>
                                        <div className='flex items-center gap-2 justify-end mt-3 md:mt-0'>
                                            <div className="relative md:w-1/4">
                                                <Input 
                                                className="pr-12 text-end text-xs" 
                                                value={builtUpDimensions.width ?? ''}
                                                placeholder="Width" 
                                                onKeyDown={Helper.allowOnlyNumbers}
                                                onChange={(e) => {
                                                    setBuiltUpDimensions((prev) => ({
                                                        ...prev,
                                                        width: e.target.value,
                                                    }))
                                                }}
                                                disabled={markAsDuplicate || edit}/>
                                                <div className='absolute inset-y-0 right-2 flex items-center'>
                                                    <BadgeSelect 
                                                    options={builtUpDimensionUnits}
                                                    value={selectedBuiltUpDimensionUnit}
                                                    onChange={setSelectedBuiltUpDimensionUnit}/>
                                                </div>
                                            </div>
                                            <span>x</span>
                                            <div className="relative md:w-1/4">
                                                <Input className="pr-12 text-end text-xs" 
                                                value={builtUpDimensions.length ?? ''}
                                                placeholder="Length" 
                                                onKeyDown={Helper.allowOnlyNumbers}
                                                onChange={(e) => {
                                                    setBuiltUpDimensions((prev) => ({
                                                        ...prev,
                                                        length: e.target.value,
                                                    }))
                                                }}
                                                disabled={markAsDuplicate || edit}/>
                                                <div className='absolute inset-y-0 right-2 flex items-center'>
                                                    <BadgeSelect 
                                                    options={builtUpDimensionUnits}
                                                    value={selectedBuiltUpDimensionUnit}
                                                    onChange={setSelectedBuiltUpDimensionUnit}/>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )
                        }
                    </>
                )
            }
            {
                selectedPropertySubType && ![19, 20, 21, 22, 23, 24, 25, 26].includes(selectedPropertySubType) &&(
                    <>
                        {/* <div className="mt-3 pt-5">
                            <Label className="text-black">Land area unit</Label>
                            <div>
                                <Button
                                size={'sm'}
                                className={`mx-1 cursor-pointer mt-3`}
                                variant={landAreaUnit === 1 ? 'default' : 'outline'}
                                onClick={(e) => { 
                                    e.preventDefault(); 
                                    setLandAreaUnit(1);
                                }}>
                                    Sqft
                                </Button>
                                <Button
                                size={'sm'}
                                className={`mx-1 cursor-pointer mt-3`}
                                variant={landAreaUnit === 2 ? 'default' : 'outline'}
                                onClick={(e) => { 
                                    e.preventDefault(); 
                                    setLandAreaUnit(2);
                                }}>
                                    Acre
                                </Button>
                            </div>
                        </div> */}
                        <div className="mt-3 pt-5 md:flex justify-between">
                            <LabelWithAsterisk label={'Land area'} className='text-black'/>
                            <div className='md:w-1/2 flex justify-end items-center mt-3 md:mt-0 relative'>
                                <Input 
                                ref={landAreaBuiltUpRef}
                                className={`text-end ${selectedLandAreaUnit === 'sqft' ? 'pr-18' : 'pr-20'} text-xs ${validationErrors.includes('landArea') && 'border-red-400'}`}
                                value={Helper.formatWithDecimal(Number(landArea))}
                                onFocus={Helper.handleFocusToEnd}
                                onClick={Helper.handleFocusToEnd}
                                onChange={(e) => {
                                    const value = Helper.handleCurrencyChange(e);
                                    setLandArea(value);
                                    removeError('landArea');
                                }}
                                disabled={markAsDuplicate || edit}
                                onKeyDown={Helper.allowOnlyNumbers}/>
                                <div className='absolute inset-y-0 right-2 flex items-center'>
                                    <BadgeSelect
                                    options={landAreaUnits}
                                    value={selectedLandAreaUnit}
                                    onChange={setSelectedLandAreaUnit}
                                    className="absolute inset-y-0 right-2"/>
                                </div>
                            </div>
                        </div>
                        {
                            validationErrors.includes('landArea') && (
                                <div className='text-end'>
                                    <span className='text-xs text-red-400'>Land area is required.</span>
                                </div>
                            )
                        }
                        <div className="mt-3 pt-5 md:flex justify-between items-center">
                            <div className=''>
                                <Label className="text-black">Land area dimensions</Label>
                            </div>
                            <div className='flex items-center gap-2 justify-end mt-3 md:mt-0'>
                                <div className='flex items-center gap-2 justify-end mt-3 md:mt-0'>
                                    <div className="relative md:w-1/4">
                                        <Input className="pr-12 text-end text-xs" 
                                        placeholder="Width" 
                                        value={landAreaDimensions.width ?? ''}
                                        onKeyDown={Helper.allowOnlyNumbers}
                                        onChange={(e) => {
                                            setLandAreaDimensions((prev) => ({
                                                ...prev,
                                                width: e.target.value,
                                            }))
                                        }}
                                        disabled={markAsDuplicate || edit}/>
                                        <div className='absolute inset-y-0 right-2 flex items-center'>
                                            <BadgeSelect 
                                            options={landAreaDimensionUnits}
                                            value={selectedLandAreaDimensionUnit}
                                            onChange={setSelectedLandAreaDimensionUnit}/>
                                        </div>
                                    </div>
                                    <span>x</span>
                                    <div className="relative md:w-1/4">
                                        <Input 
                                        className="pr-12 text-end text-xs" 
                                        placeholder="Length"
                                        value={landAreaDimensions.length ?? ''} 
                                        onKeyDown={Helper.allowOnlyNumbers}
                                        onChange={(e) => {
                                            setLandAreaDimensions((prev) => ({
                                                ...prev,
                                                length: e.target.value,
                                            }))
                                        }}
                                        disabled={markAsDuplicate || edit}/>
                                        <div className='absolute inset-y-0 right-2 flex items-center'>
                                            <BadgeSelect 
                                            options={landAreaDimensionUnits}
                                            value={selectedLandAreaDimensionUnit}
                                            onChange={setSelectedLandAreaDimensionUnit}/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )
            }
            {
                selectedPropertySubType && ![4, 18].includes(selectedPropertySubType) && (
                    <>
                        {
                            // Industrial Land
                            // Agriculture Land
                            ![32, 38, 39, 40].includes(selectedPropertySubType) && (
                                <>
                                    <hr className='my-5'/>
                                    <h5 className='h5'>Floor Details</h5>
                                    <div className='mt-3 md:flex justify-between'>
                                        <Label className="text-black">Floor details</Label>
                                        <div className="flex md:w-1/2 gap-2 md:mt-0 mt-3">
                                            <Input
                                            className="w-1/3 text-xs text-end"
                                            placeholder="Block"
                                            value={floorDetails.block}
                                            onChange={(e) =>
                                                setFloorDetails((prev) => ({ ...prev, block: e.target.value }))
                                            }
                                            disabled={markAsDuplicate || edit}/>
                                            <Input
                                            className="w-1/3 text-xs text-end"
                                            placeholder="Floor"
                                            value={floorDetails.floor}
                                            onChange={(e) =>
                                                setFloorDetails((prev) => ({ ...prev, floor: e.target.value }))
                                            } 
                                            disabled={markAsDuplicate || edit}/>
                                            <Input
                                            className="w-1/3 text-xs text-end"
                                            placeholder="Unit/House no."
                                            value={floorDetails.unit}
                                            onChange={(e) =>
                                                setFloorDetails((prev) => ({ ...prev, unit: e.target.value }))
                                            } 
                                            disabled={markAsDuplicate || edit}/>
                                            </div>
                                    </div>
                                    <div className='flex mt-5'>
                                        <Checkbox 
                                        checked={hideBlockNoWhenPublished} 
                                        onCheckedChange={(val) => {
                                            setHideBlockNoWhenPublished(!hideBlockNoWhenPublished);
                                        }}
                                        disabled={markAsDuplicate || edit}/>
                                        <Label className="text-black ml-1">Upon publication, conceal the Block, Unit/House Number, and Floor details.</Label>
                                    </div>
                                    <div className='mt-5 md:flex justify-between'>
                                        <Label className="text-black">Floor loading</Label>
                                        <div className="w-full md:w-1/2 mt-3 md:mt-0">
                                            <div className='relative'>
                                                <Input
                                                className={`pr-18 text-end text-xs ${validationErrors.includes('floorLoading') && 'border-red-400'}`}
                                                placeholder="What is the floor capacity?"
                                                value={floorLoading}
                                                onKeyDown={Helper.allowOnlyNumbers}
                                                onChange={(e) => {
                                                    setFloorLoading(e.target.value);
                                                    removeError('floorLoading');
                                                }}
                                                disabled={markAsDuplicate || edit}/>
                                                <div className="absolute inset-y-0 right-2 flex items-center">
                                                    <BadgeSelect 
                                                    options={floorLoadingUnits}
                                                    value={selectedFloorLoadingUnit}
                                                    onChange={setSelectedFloorLoadingUnit}/>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <hr className='my-5'/>
                                    <h5 className='h5'>Ceiling Height</h5>
                                    <div className="mt-3 flex items-center justify-between">
                                        <Label className="text-black">Ceiling Height</Label>
                                        <div className="w-full md:w-1/2 mt-3 md:mt-0">
                                            <div className='relative'>
                                                <Input
                                                className={`pr-12 text-end text-xs ${validationErrors.includes('ceilingHeight') && 'border-red-400'}`}
                                                placeholder="How tall is your ceiling?"
                                                value={ceilingHeight}
                                                onKeyDown={Helper.allowOnlyNumbers}
                                                onChange={(e) => {
                                                    setCeilingHeight(e.target.value);
                                                    removeError('ceilingHeight');
                                                }}
                                                disabled={markAsDuplicate || edit}/>
                                                <div className="absolute inset-y-0 right-2 flex items-center">
                                                    <BadgeSelect 
                                                    options={ceilingHeightUnits}
                                                    value={selectedCeilingHeightUnit}
                                                    onChange={setSelectedCeilingHeightUnit}/>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <hr className='my-5'/>
                                    <h5 className='h5'>Parking</h5>
                                    <div className="mt-3 flex items-center justify-between">
                                        <Label className="text-black">Parking spots (optional)</Label>
                                        <div className="flex items-center justify-end gap-2">
                                            <Button 
                                            type="button" 
                                            variant="outline" 
                                            size="icon" 

                                            disabled={!noOfParkingSpots || markAsDuplicate || edit}
                                            onClick={() => setNoOfParkingSpots(prev => (prev && prev > 0 ? prev - 1 : 0))}>
                                                <Minus />
                                            </Button>
                                            <Button variant='ghost' className="pointer-events-none text-black">{noOfParkingSpots ?? 'Select'}</Button>
                                            <Button 
                                            type="button" 
                                            variant="outline" 
                                            size="icon" 

                                            disabled={markAsDuplicate || edit}
                                            onClick={() =>
                                                setNoOfParkingSpots(prev => (prev === null ? 0 : prev + 1))
                                            }>
                                                <Plus />
                                            </Button>
                                        </div>
                                    </div>
                                    <hr className='my-5'/>
                                    <div className='flex items-center'>
                                        <h5 className='h5'>Furnishing</h5>
                                    </div>
                                    {/* <LabelWithAsterisk label={'Furnishing'} className='text-black'/> */}
                                    <div className='mt-3' ref={furnishingRef}>
                                        {
                                            furnishingConditions.map((condition) => {
                                                return (
                                                    <Button 
                                                    type='button'
                                                    key={condition.value}
                                                    size={'sm'}
                                                    variant={selectedFurnishingCondition === condition.value ? 'default' : 'outline'}
                                                    className={`mx-1 cursor-pointer ${validationErrors.includes('furnishing') && 'border-red-400'} ${selectedFurnishingCondition !== condition.value && 'text-black'}`}
                                                    onClick={() => {
                                                        setSelectedFurnishingCondition(condition.value);
                                                        removeError('furnishing');
                                                    }}
                                                    disabled={markAsDuplicate || edit}>
                                                        { condition.label }
                                                    </Button>
                                                )
                                            })
                                        }
                                    </div>
                                    {
                                        validationErrors.includes('furnishing') && (
                                            <div>
                                                <span className='text-xs text-red-400'>Furnishing condition is required.</span>
                                            </div>
                                        )
                                    }
                                    <hr className='my-5'/>
                                    <h5 className='h5'>Unit Features</h5>
                                    <div className="">
                                        <div className={`flex flex-wrap gap-2 ${selectedUnitFeatures.length > 0 && 'mt-3'}`}>
                                            {
                                                unitFeatures && unitFeatures.length > 0 ? (
                                                    unitFeatures?.filter((b) => selectedUnitFeatures.includes(b.id))
                                                        .map((feature) => (
                                                            <Button type='button' 
                                                            key={feature.id} 
                                                            size="sm" 
                                                            variant="outline" 

                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setSelectedUnitFeatures((prev) =>
                                                                    prev.filter((id) => id !== feature.id)
                                                                )
                                                            }}
                                                            disabled={markAsDuplicate || edit}>
                                                                {feature.name}
                                                                <MinusCircle 
                                                                className='text-red-400 cursor-pointer'
                                                                onClick={(e) => {
                                                                    e.stopPropagation(); 
                                                                }}/>
                                                            </Button>
                                                        ))
                                                    ) : (
                                                    <span className="text-sm text-muted-foreground">No features selected</span>
                                                )
                                            }
                                        </div>
                                        <Dialog>
                                            <DialogTrigger asChild>
                                                <Button size="sm" variant="secondary" className='mt-3' disabled={markAsDuplicate || edit}>
                                                Select Features
                                                </Button>
                                            </DialogTrigger>
                                            <DialogContent className='max-h-[90vh] overflow-y-auto'>
                                                <DialogHeader>
                                                    <DialogTitle>Unit Features</DialogTitle>
                                                </DialogHeader>
                                                <div className="grid gap-6 mt-4">
                                                {
                                                    unitFeatures && unitFeatures.length > 0 ? (
                                                        Object.entries(
                                                            unitFeatures.reduce((groups, feature) => {
                                                            const category = feature.category || "Others";
                                                            if (!groups[category]) groups[category] = [];
                                                            groups[category].push(feature);
                                                            return groups;
                                                            }, {} as Record<string, typeof unitFeatures>)
                                                        ).map(([category, features]) => (
                                                            <div key={category}>
                                                            {/* Category Title */}
                                                            <h3 className="text-md font-semibold text-gray-700 mb-2">{category}</h3>
                                                            
                                                            <div className="grid gap-3">
                                                                {
                                                                    features.map((feature) => (
                                                                        <div key={feature.id} className="flex items-center space-x-2">
                                                                            <Checkbox
                                                                            id={`feature-${feature.id}`}
                                                                            checked={selectedUnitFeatures.includes(feature.id)}
                                                                            onCheckedChange={(checked) => {
                                                                                if (checked) {
                                                                                setSelectedUnitFeatures((prev) => [...prev, feature.id]);
                                                                                } else {
                                                                                setSelectedUnitFeatures((prev) =>
                                                                                    prev.filter((id) => id !== feature.id)
                                                                                );
                                                                                }
                                                                            }}
                                                                            />
                                                                            <label
                                                                            htmlFor={`feature-${feature.id}`}
                                                                            className="text-sm font-medium leading-none text-black">
                                                                            {feature.name}
                                                                            </label>
                                                                        </div>
                                                                    ))
                                                                }
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <p className="text-sm text-gray-400">No features available.</p>
                                                    )
                                                }
                                                </div>
                                            </DialogContent>
                                        </Dialog>
                                    </div>
                                </>
                            )
                        }
                        {
                            selectedPropertyType && selectedPropertyType > 4 && (
                                <>
                                    <hr className='my-5'/>
                                    <h5 className='h5'>Electricity</h5>
                                    <div className='mt-3 pt-5'>
                                        <Label className='text-black'>Electricity Phase</Label>
                                        <div className='mt-3'>
                                        {
                                            electricityPhases.map((phase) => {
                                                return (
                                                    <Button 
                                                    type='button'
                                                    key={phase.value}
                                                    size={'sm'}
                                                    variant={selectedElectricityPhase === phase.value ? 'default' : 'outline'}
                                                    className={`mx-1 cursor-pointer ${selectedElectricityPhase !== phase.value && 'text-black'}`}
                                                    onClick={() => {
                                                        setSelectedElectricityPhase(phase.value);
                                                    }}
                                                    disabled={markAsDuplicate || edit}>
                                                        { phase.label }
                                                    </Button>
                                                )
                                            })
                                        }
                                        </div>
                                    </div>
                                    <div className='mt-3 pt-5'>
                                        <Label className='text-black'>Electricity Supply </Label>
                                        <div className='mt-3 relative'>
                                            <Input 
                                            className='text-end pr-16' 
                                            value={electricitySupply ?? ''} 
                                            onKeyDown={Helper.allowOnlyNumbers}
                                            onChange={(e) => {
                                                setElectricitySupply(Number(e.target.value));
                                            }}
                                            disabled={markAsDuplicate || edit}/>
                                            <div className='absolute inset-y-0 right-2 flex items-center'>
                                                <BadgeSelect
                                                options={electricitySupplyUnits}
                                                value={selectedElectricitySupplyUnit}
                                                onChange={setSelectedElectricitySupplyUnit}
                                                className="absolute inset-y-0 right-2"/>
                                            </div>
                                        </div>
                                    </div>
                                    <hr className='my-5'/>
                                    <h5 className='h5'>Lifts</h5>
                                    <div className="mt-3 pt-5 flex items-center justify-between">
                                        <Label className="text-black">Total cargo lifts</Label>
                                        <div className="flex items-center justify-end gap-2">
                                            <Button 
                                            type="button" 
                                            variant="outline" 
                                            size="icon" 

                                            disabled={!totalCargoLifts || markAsDuplicate || edit}
                                            onClick={() => setTotalCargoLifts(prev => (prev && prev > 0 ? prev - 1 : 0))}>
                                                <Minus />
                                            </Button>
                                            <Button variant='ghost' className="pointer-events-none text-black">{totalCargoLifts ?? 'Select'}</Button>
                                            <Button 
                                            type="button" 
                                            variant="outline" 
                                            size="icon" 

                                            disabled={markAsDuplicate || edit}
                                            onClick={() =>
                                                setTotalCargoLifts(prev => (prev === null ? 0 : prev + 1))
                                            }>
                                                <Plus />
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="mt-3 pt-5 flex items-center justify-between">
                                        <Label className="text-black">Total Passenger lifts</Label>
                                        <div className="flex items-center justify-end gap-2">
                                            <Button 
                                            type="button" 
                                            variant="outline" 
                                            size="icon" 

                                            disabled={!totalPassengerLifts || markAsDuplicate || edit}
                                            onClick={() => setTotalPassengerLifts(prev => (prev && prev > 0 ? prev - 1 : 0))}>
                                                <Minus />
                                            </Button>
                                            <Button variant='ghost' className="pointer-events-none text-black">{totalPassengerLifts ?? 'Select'}</Button>
                                            <Button 
                                            type="button" 
                                            variant="outline" 
                                            size="icon" 

                                            disabled={markAsDuplicate || edit}
                                            onClick={() =>
                                                setTotalPassengerLifts(prev => (prev === null ? 0 : prev + 1))
                                            }>
                                                <Plus />
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="mt-3 pt-5">
                                        <Label className="text-black">Maximum Lift Capacity <Badge variant={'secondary'}>KG</Badge></Label>
                                        <Input 
                                        type='number'
                                        className='mt-3 text-end' 
                                        value={maximumLiftCapacity ?? ''} 
                                        onKeyDown={Helper.allowOnlyNumbers}
                                        disabled={markAsDuplicate || edit}
                                        onChange={(e) => {
                                            setMaximumLiftCapacity(Number(e.target.value))
                                        }}/>
                                    </div>
                                </>
                            )
                        }
                    </>
                )
            }
        </section>
    )
})

export default UnitDetails